import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiFileText, 
  FiBook, 
  FiImage, 
  FiUsers, 
  FiBriefcase, 
  FiStar,
  FiTrendingUp,
  FiArrowRight,
  FiPlus
} from 'react-icons/fi';
import { Card, Spinner, Badge } from '../../components/common';
import { dashboardService } from '../../services/dashboardService';
import { formatDate } from '../../utils/dateUtils';
import './DashboardPage.css';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await dashboardService.getAll();
      setStats(response.data.stats);
      setRecentActivity(response.data.recentActivity);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      key: 'totalNews', 
      label: 'Total News', 
      icon: FiFileText, 
      color: 'blue',
      link: '/news'
    },
    { 
      key: 'totalBlogs', 
      label: 'Total Blogs', 
      icon: FiBook, 
      color: 'orange',
      link: '/blogs'
    },
    { 
      key: 'totalGalleryImages', 
      label: 'Gallery Images', 
      icon: FiImage, 
      color: 'green',
      link: '/gallery'
    },
    { 
      key: 'totalClients', 
      label: 'Total Clients', 
      icon: FiUsers, 
      color: 'purple',
      link: '/clients'
    },
    { 
      key: 'totalServices', 
      label: 'Total Services', 
      icon: FiBriefcase, 
      color: 'cyan',
      link: '/services'
    },
    { 
      key: 'totalReviews', 
      label: 'Total Reviews', 
      icon: FiStar, 
      color: 'yellow',
      link: '/reviews'
    },
  ];

  const getActivityIcon = (type) => {
    const icons = {
      news: FiFileText,
      blog: FiBook,
      gallery: FiImage,
      client: FiUsers,
      service: FiBriefcase,
      review: FiStar,
    };
    return icons[type] || FiFileText;
  };

  const getActivityBadge = (action) => {
    const badges = {
      created: { variant: 'success', label: 'Created' },
      updated: { variant: 'primary', label: 'Updated' },
      deleted: { variant: 'danger', label: 'Deleted' },
      published: { variant: 'success', label: 'Published' },
      approved: { variant: 'orange', label: 'Approved' },
      uploaded: { variant: 'primary', label: 'Uploaded' },
    };
    return badges[action] || { variant: 'default', label: action };
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-content">
          <h2>Welcome to Admin Panel</h2>
          <p>Here's what's happening with your website today.</p>
        </div>
        <div className="quick-actions">
          <Link to="/news/add" className="quick-action-btn">
            <FiPlus /> Add News
          </Link>
          <Link to="/blogs/add" className="quick-action-btn">
            <FiPlus /> Add Blog
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link to={stat.link} key={stat.key} className={`stat-card stat-${stat.color}`}>
              <div className="stat-icon">
                <Icon />
              </div>
              <div className="stat-content">
                <span className="stat-value">{stats?.[stat.key] || 0}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
              <FiTrendingUp className="stat-trend" />
            </Link>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="dashboard-section">
        <Card 
          title="Recent Activity" 
          subtitle="Latest updates across all modules"
          action={
            <Link to="#" className="view-all-link">
              View All <FiArrowRight />
            </Link>
          }
        >
          <div className="activity-list">
            {recentActivity.length === 0 ? (
              <p className="no-activity">No recent activity</p>
            ) : (
              recentActivity.map((activity) => {
                const Icon = getActivityIcon(activity.type);
                const badge = getActivityBadge(activity.action);
                return (
                  <div key={activity.id} className="activity-item">
                    <div className={`activity-icon activity-icon-${activity.type}`}>
                      <Icon />
                    </div>
                    <div className="activity-content">
                      <p className="activity-title">{activity.title}</p>
                      <div className="activity-meta">
                        <Badge variant={badge.variant} size="small">
                          {badge.label}
                        </Badge>
                        <span className="activity-time">
                          {formatDate(activity.timestamp, 'relative')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
